/**
 * @file LookSpaceGroup.tsx
 * @description Группа «Отступы» секции (phase19.16 Р2; SB100; §7.13 фазы).
 * SB120 этап 1 «секция-герой» (uiux-sb120-stage1 §2.2, §5; данные
 * db-design-sb120-stage1 §1): шесть чисел вместо ступеней —
 *
 * ```
 * Снаружи сверху / снизу · Внутри сверху / снизу    (0–200 px, ↺ как у сайта)
 * [⛓ Одинаково слева и справа] Внутри слева и справа (0–120 px)
 *   под каждым — «на телефоне N px · Своё ›»
 * Наезд на блок выше  0–200 px  [0] [L 64] [XL 96]
 * [ ] Слить с блоком выше        (вместе с наездом нельзя)
 * На телефоне отступы блока  0–100 %  ↺ как у сайта 60 %  [Плотнее 30] [Как на ПК 100]
 * ```
 *
 * Ступени-подсказки — шкала сайта × плотность магазина. «⛓» не хранится:
 * связь — когда слева и справа равны (по умолчанию оба «как у сайта»).
 * Доля для телефона действует только на шесть полей; расстояния в колонках,
 * наезд и выход фото за край берут долю сайта.
 */

import type { SectionLook, SectionSpace } from "@chaiberi/shared";
import { useState } from "react";

import { CheckRow } from "../../../site-view/CheckRow";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { LinkedPairField } from "../fields/LinkedPairField";
import { useSectionForm } from "../section-form-context";
import { SpaceSideField } from "./SpaceSideField";
import { ownValue } from "../../../../lib/site-fields/number-input";
import { setPhoneSide, setSpaceSide, siteSideHint, sidesLinked, spacePresets, SPACE_SIDE_LABELS, type SpaceTheme } from "../../../../lib/site-builder/look-space";

const PERCENT = { factor: 100, decimals: 0 } as const;

interface Props {
  look: SectionLook;
  theme: SpaceTheme;
  onChange: (space: SectionSpace) => void;
}

export function LookSpaceGroup({ look, theme, onChange }: Props) {
  const { highlight, preview } = useSectionForm();
  const space = look.space;
  // Расцеплено владельцем или уже разные — два поля.
  const [split, setSplit] = useState(() => !sidesLinked(space) || !sidesLinked(space.phone ?? {}));
  const linked = !split && sidesLinked(space);
  const set = (patch: Partial<SectionSpace>) => onChange({ ...space, ...patch });
  const field = (side: keyof typeof SPACE_SIDE_LABELS) => <SpaceSideField key={side} sides={[side]} label={SPACE_SIDE_LABELS[side]} look={look} theme={theme} onChange={onChange} />;

  function onLink(next: boolean) {
    setSplit(!next);
    if (!next) return;
    // Сцепили разные — правая сторона берёт значение левой (и на телефоне).
    const withRight = setSpaceSide(space, "innerRight", space.innerLeft);
    onChange(setPhoneSide(withRight, "innerRight", space.phone?.innerLeft));
  }

  return (
    <div className="flex flex-col gap-4">
      {field("outerTop")}
      {field("outerBottom")}
      {field("innerTop")}
      {field("innerBottom")}
      <LinkedPairField
        linked={linked}
        onLink={onLink}
        label="Внутри слева и справа"
        both={<SpaceSideField sides={["innerLeft", "innerRight"]} label="Внутри слева и справа" look={look} theme={theme} onChange={onChange} hint={siteSideHint(theme.density, look)} />}
        left={<SpaceSideField sides={["innerLeft"]} label={SPACE_SIDE_LABELS.innerLeft} look={look} theme={theme} onChange={onChange} hint={siteSideHint(theme.density, look)} />}
        right={field("innerRight")}
      />
      <div className="flex flex-col gap-1" onMouseEnter={() => highlight("overlap")} onMouseLeave={() => highlight(null)}>
        <NumberSliderField
          label="Наезд на блок выше"
          value={ownValue(space.overlap, 0)}
          inherit={{ value: 0, label: "нет" }}
          min={0}
          max={200}
          step={1}
          unit="px"
          display={{ factor: 1, decimals: 2 }}
          presets={spacePresets(theme.density, ["0", "l", "xl"])}
          disabled={space.mergeWithPrev}
          hint={space.mergeWithPrev ? "Наезд нельзя, пока блок слит с блоком выше." : undefined}
          onChange={(v) => set({ overlap: v ?? 0 })}
          onPreview={(v) => preview("look.space.overlap", v ?? 0)}
        />
      </div>
      <CheckRow checked={space.mergeWithPrev} disabled={space.overlap > 0} onChange={(mergeWithPrev) => set({ mergeWithPrev })}>
        Слить с блоком выше — общий фон без шва
      </CheckRow>
      {space.overlap > 0 ? <p className="type-caption text-on-surface-variant">Слить нельзя, пока включён наезд.</p> : null}
      <NumberSliderField
        label="Отступы на телефоне"
        value={space.phoneFactor}
        inherit={{ value: theme.phoneFactor, label: `как у сайта ${Math.round(theme.phoneFactor * 100)} %` }}
        min={0}
        max={1}
        step={0.01}
        largeStep={0.1}
        unit="%"
        display={PERCENT}
        presets={[
          { label: "Плотнее 30", value: 0.3 },
          { label: "Как на ПК 100", value: 1 },
        ]}
        hint="Доля от ПК для шести полей выше. «Своё» у поля — важнее доли."
        onChange={(phoneFactor) => onChange(phoneFactor === undefined ? withoutFactor(space) : { ...space, phoneFactor })}
        onPreview={(v) => preview("look.space.phoneFactor", v)}
      />
    </div>
  );
}

/** «↺ как у сайта» — ключ доли снимается. */
function withoutFactor(space: SectionSpace): SectionSpace {
  const next = { ...space };
  delete next.phoneFactor;
  return next;
}
